/**
 * CSV → message-record projection for the TF911
 * `/conversation-history/download` endpoint. Lives in its own module so
 * `textFrom911Api.js` can stay focused on RTK Query wiring and so the
 * parser can be unit-tested without spinning up the API.
 */

export interface ConversationMessageRecord {
  /** Synthetic id; the CSV doesn't carry the TalkJS message id. */
  id: string;
  /** `conversation_id` column — has no `tf911.` prefix. */
  cleanedConversationId: string;
  senderId: string | null;
  /** `original_message` column. */
  text: string;
  /** UNIX timestamp in milliseconds, parsed from the CSV's UTC string. */
  createdAt: number;
}

// Column layout written by `DownloadConversationHistory.generate_csv_content`
// in `text_from_911_app.py`. Indices must stay in sync with that writer or
// the parsed records below will be wrong.
const CSV_COL_CONVERSATION_ID = 0;
const CSV_COL_SENDER_ID = 2;
const CSV_COL_TIMESTAMP = 4;
const CSV_COL_ORIGINAL_MESSAGE = 5;
const CSV_COL_MESSAGE_TYPE = 11;
const CSV_COLUMN_COUNT = 12;

/**
 * Tiny RFC4180 CSV parser sufficient for the TF911 download payload.
 * The BE writes via Python's `csv.writer`, so we only need to handle:
 *   - Comma-separated fields
 *   - Fields wrapped in `"` to escape commas / newlines
 *   - `""` to represent a literal `"` inside a quoted field
 *   - `\r\n` or `\n` row terminators
 * No reason to pull in papaparse for ~12 columns; if the FE ever needs a
 * full-featured parser, swap this out.
 */
const parseCsv = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field);
      field = '';
    } else if (ch === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else if (ch !== '\r') {
      field += ch;
    }
  }

  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
};

// "YYYY-MM-DD HH:MM:SS UTC" → epoch ms. Mirrors
// `utils.convert_talkjs_timestamp_to_readable` on the BE. Returns NaN on
// malformed input so the caller can drop the row.
const parseCsvTimestamp = (ts: string | undefined): number => {
  if (!ts) return Number.NaN;
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2}) UTC$/.exec(ts);
  if (!m) return Number.NaN;
  return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
};

/**
 * Project the BE's CSV download into the structured shape
 * `useLoadCallQueueMessages` expects. We deliberately drop:
 *   - The header row
 *   - MMS rows (no searchable text; column 5 is the attachment URL)
 *   - Rows whose `original_message` is empty (system msgs without text,
 *     translation-only artifacts, etc.)
 * The CSV's `conversation_id` column is the cleaned id (without the
 * `tf911.` prefix the FE stores), so callers must remember which id they
 * asked for and use that as the canonical key.
 */
export const csvToMessageRecords = (
  csvText: string | null | undefined,
): ConversationMessageRecord[] => {
  if (!csvText) return [];
  const rows = parseCsv(csvText);
  const records: ConversationMessageRecord[] = [];
  for (let i = 1; i < rows.length; i += 1) {
    const row = rows[i];
    if (!row || row.length < CSV_COLUMN_COUNT) continue;
    const messageType = row[CSV_COL_MESSAGE_TYPE];
    if (messageType === 'MMS') continue;
    const text = row[CSV_COL_ORIGINAL_MESSAGE];
    if (!text) continue;
    const timestamp = parseCsvTimestamp(row[CSV_COL_TIMESTAMP]);
    if (Number.isNaN(timestamp)) continue;
    const cleanedConversationId = row[CSV_COL_CONVERSATION_ID];
    const senderId = row[CSV_COL_SENDER_ID] || null;
    records.push({
      // CSV doesn't carry the TalkJS message id; synthesize a stable
      // composite so the slice can dedupe across refreshes.
      id: `${cleanedConversationId}|${timestamp}|${senderId ?? ''}`,
      cleanedConversationId,
      senderId,
      text,
      createdAt: timestamp,
    });
  }
  return records;
};
